<template>
  <section
    class="three-ct-container"
    :data-ab-id="dataAbId"
  >
    <KikHeading
      :title="topTitle"
      :description="subText"
    />
    <!----------------  MOBILE VIEW ---------------->
    <div
      v-if="teaserData.length > 0"
      class="three-wrapper-carousel"
    >
      <KikCarousel>
        <div
          v-for="(item, i) in teaserData"
          :key="i"
          class="relative w-[70vw] shrink-0 rounded-md first:ms-auto last:me-auto hover:shadow-lg md:w-[250px] lg:w-[350px] xl:w-[450px]"
        >
          <SharedLink
            v-if="item.ctaLink"
            :link="item.ctaLink"
          >
            <NuxtImg
              :src="item.src"
              :srcset="item.srcset"
              :alt="item.title"
              :loading="imageLoadType"
              :preload="imageLoadType === 'eager'"
              :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
            />
          </SharedLink>
          <section class="three-wrapper__content">
            <h2
              v-if="item.title"
              class="flex gap-1"
            >
              {{ item.title }}
              <KikLegalInformationDrawer
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'title'"
                :legal-text="item.legalText"
                :title="item.legalTitle"
              />
            </h2>
            <p class="flex gap-1">
              {{ item.subtitle }}
              <KikLegalInformationDrawer
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'subtitle'"
                :legal-text="item.legalText"
                :title="item.legalTitle"
              />
            </p>
            <SharedLink
              v-if="item.ctaLink "
              :link="item.ctaLink"
            >
              <span
                :style="{ color: `${item.ctaTextColor}` }"
                class="uppercase"
              >{{ item.ctaText || $t('Shop now') }}</span>
              <SfIconArrowForward
                class="fill-white"
                size="sm"
              />
            </SharedLink>
          </section>
        </div>
      </KikCarousel>
    </div>

    <!----------------  DESKTOP VIEW ---------------->
    <div class="three-wrapper">
      <div
        v-for="(item, i) in teaserData"
        :key="i"
        class="three-wrapper__item"
      >
        <SharedLink
          v-if="item.ctaLink "
          :link="item.ctaLink"
        >
          <NuxtImg
            :src="item.src"
            sizes="lg:100vw 48vw"
            :srcset="item.srcset"
            :alt="item.title"
            :loading="imageLoadType"
            :preload="imageLoadType === 'eager'"
            :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
          />
          <section class="three-wrapper__content">
            <h2
              v-if="item.title"
              class="flex items-end gap-1"
            >
              {{ item.title }}
              <KikLegalInformationDrawer
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'title'"
                :legal-text="item.legalText"
                :title="item.legalTitle"
              />
            </h2>
            <p class="flex items-end gap-1">
              {{ item.subtitle }}
              <KikLegalInformationDrawer
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'subtitle'"
                :legal-text="item.legalText"
                :title="item.legalTitle"
              />
            </p>
            <span
              :style="{ color: `${item.ctaTextColor}` }"
              class="uppercase"
            >{{ item.ctaText || $t('Shop now') }}</span>
            <SfIconArrowForward
              class="ml-1 fill-white"
              size="sm"
            />
          </section>
        </SharedLink>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { SfIconArrowForward } from '@storefront-ui/vue';
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  topTitle?: string;
  topTitleColor?: string;
  subText?: string;
  subTextColor: string;
  slides?: {
    title: string;
    subTitle: string;
    image: string;
    ctaLink: any[];
    intLinkParam?: string;
    ctaText: string;
    ctaTextColor: string;
    enableLegalDisclaimer: boolean;
    legalText: string;
    legalTitle: string;
    titleOrSubtitle: string;
    aspectRatio: string;
    mobileAspectRatio?: string;
  }[];
  intLinkParam?: string;
  imageLoadType?: HTMLImageElement['loading'];
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  topTitle: '',
  topTitleColor: '',
  subText: '',
  subTextColor: '',
  slides: () => [],
  imageLoadType: 'lazy',
  intLinkParam: '',
});

const slides = toRef(props, 'slides');
const { $domain } = useNuxtApp();

const teaserData = computed(() => (
  slides.value?.map(slide => ({
    title: slide.title,
    subtitle: slide.subTitle,
    src: magnoliaGetters.getDefaultSrcAsset(slide.image, $domain.cmsAssetDomainPath),
    srcset: magnoliaGetters.getSrcSetImagesAsString(slide.image, $domain.cmsAssetDomainPath, slide.aspectRatio, slide.mobileAspectRatio),
    ctaLink: slide.ctaLink?.[0] ? magnoliaGetters.getLinkAndName(slide.ctaLink[0], $domain.cmsAssetDomainPath, slide.ctaLink[0].switchable?.intLinkParam) : {},
    ctaText: slide.ctaLink?.[0] ? slide.ctaLink?.[0].ctaText : '',
    ctaTextColor: slide.ctaTextColor,
    enableLegalDisclaimer: slide.enableLegalDisclaimer,
    legalText: slide.legalText,
    legalTitle: slide.legalTitle,
    titleOrSubtitle: slide.titleOrSubtitle,
  }))
));
</script>

<style lang="scss" scoped>
  .three-ct-container {
    padding: 0 80px;
    margin-bottom: 40px;

    // Mobile and Tablet
    @media (max-width: $tablet-max) {
      padding: 0;
      margin-left: 20px;
      margin-bottom: 20px;
    }
    h2 {
      color: v-bind(topTitleColor);
      font-size: 30px;
      font-weight: 700;

      // Mobile and Tablet
      @media (max-width: $mobile-max) {
        font-size: 20px;
      }
    }

    p {
      color: v-bind(subTextColor);
      margin-bottom: 1rem;
    }
  }

  .three-wrapper {
    // Mobile and tablet
    @media (max-width: $tablet-max) {
      display: none;
    }
    position: relative;
    display: flex;
    gap: 20px;
    margin-top: 20px;

    &__item {
      height: 500px;
      width: 100%;
      overflow: hidden;

      img {
        min-height: 500px;
        width: 100%;
        object-fit: cover;
      }
    }
    &__content {
      position: absolute;
      padding: 20px;
      bottom: 0;
      font-size: var(--font-size--base);
      background-image: linear-gradient(180deg, rgba(255, 255, 255, 0), rgba(0, 0, 0, 0.5));
      width: calc((100% / 3) - 13px);

      h2, p {
        color: var(--c-white);
      }

      :deep(a) {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: var( --font-size--sm);
        font-weight: 700;
        text-transform: uppercase;
        text-decoration: none;
        letter-spacing: 1px;
      }

      // Mobile and tablet
      @media (max-width: $tablet-max) {
        width: 100%;
      }
    }
  }

  .three-wrapper-carousel {
    display: none;

    // Mobile and tablet
    @media (max-width: $tablet-max) {
      display: initial;
    }
  }
</style>
